import { el, clear } from "../components/dom.js";
import { progressField, priceNoteField, loanField, startAgainButton } from "../components/tracking-fields.js";
import { reviewField, tagsField as myTagsField } from "../components/review-tags.js";
import { tagSuggestions } from "../utils/filters.js";
import { inTheNews } from "../components/in-the-news.js";
import { peopleRow } from "../components/people-row.js";
import { offerUndoDelete } from "../components/undo.js";
import { historySection } from "../components/history-section.js";
import {
	ENGAGEMENT_STATUSES,
	OWNERSHIP_STATUSES,
	AWARDS,
	awardLabel,
	statusLabel,
} from "../components/status-badge.js";
import {
	getAnimeById,
	updateAnime,
	deleteAnime,
	setAnimeRating,
	setAnimeAwards,
	setAnimeNote,
	setSeasonSeen,
	getCatalog,
	saveCatalog,
	effectiveRating,
	effectiveSeen,
	NOTE_MAX_LENGTH,
} from "../db/anime.js";
import { getAnimeEpisodes } from "../api/anime.js";
import { groupsSection } from "../components/groups-section.js";
import { getGroupsContainingItem } from "../db/groups.js";
import { formatDate, formatReleaseDate, todayDate } from "../utils/dates.js";
import {
	seasonReleased,
	seasonPremiereDate,
	isCatalogStale,
} from "../utils/seasons.js";
import { navigate, navigateBack, rerenderRoute } from "../main.js";
import { openItemMenu } from "../components/item-menu.js";
import { overriddenFields } from "../utils/overrides.js";
import { statusSticker } from "../components/magazine/status.js";
import {
	magDetailHero,
	magSummary,
	magField,
	magSelectRow,
	magChips,
	magRating,
	magNote,
	magDeleteFooter,
	magDateLine,
} from "../components/magazine/detail.js";
import { MAG, magGlass, magCover, magButton } from "../components/magazine/kit.js";

export async function renderAnimeDetail(root, id) {
	const anime = await getAnimeById(id);
	if (!anime) {
		root.append(
			el(
				"div.view",
				{},
				el("p", {}, "Anime not found."),
				el(
					"button.btn",
					{ type: "button", onclick: () => navigateBack("/anime") },
					"Back",
				),
			),
		);
		return;
	}

	const view = el("div.view", {});
	root.append(view);
	const cached = await getCatalog(anime.tvmaze_id);
	mount(view, anime, cached);

	// Lazy-load the season/episode catalog (cached locally, re-fetchable), and
	// re-fetch a STALE one: older than CATALOG_MAX_AGE_DAYS (premiere dates shift,
	// new seasons get announced) or predating the `premiere_date` field. Silent —
	// when the data changed, the view re-renders. The daily pass in
	// db/catalog-refresh.js refreshes catalogs in the background too.
	if ((!cached || isCatalogStale(cached)) && anime.tvmaze_id) {
		getAnimeEpisodes(anime.tvmaze_id)
			.then(async (data) => {
				if (!data) return;
				const saved = await saveCatalog(data);
				mount(view, await getAnimeById(id), saved);
			})
			.catch(() => {});
	}
}

async function mount(view, anime, catalog) {
	// Re-mounting rebuilds the whole view. Capture scroll up-front and do the async
	// work (group membership) WHILE THE OLD VIEW IS STILL ON SCREEN; only clear +
	// append + restore synchronously at the very end, so the browser never paints a
	// cleared/top state (that intermediate paint was the scroll jump, then flash).
	const scrollY = window.scrollY;

	const groups = await getGroupsContainingItem(anime);

	// ---- feature hero ----
	const kickerBits = ["Anime"];
	if (anime.premiered_year) kickerBits.push(String(anime.premiered_year));

	const figures = [{ value: anime.rating != null ? anime.rating : "—", label: "Rating" }];
	if (anime.mal_source) figures.push({ value: anime.mal_source, label: "Source" });

	if (overriddenFields(anime).length > 0) kickerBits.push("Edited");

	const hero = magDetailHero(anime, {
		kicker: kickerBits.join(" · "),
		title: anime.title,
		sticker: statusSticker(anime.engagement_status, "anime"),
		figures,
		tag: (anime.awards ?? [])[0] ? awardLabel(anime.awards[0]) : null,
		onBack: () => navigateBack("/anime"),
		// "⋯": edit details / re-link / undo / reset (components/item-menu.js). The
		// catalog supplies season posters as cover candidates.
		onMenu: () =>
			openItemMenu({
				mediaType: "anime",
				record: anime,
				catalog,
				onChanged: (updated, { relinked } = {}) =>
					relinked ? rerenderRoute() : mount(view, updated, catalog),
			}),
	});

	// ---- read-only MAL taxonomy chips ----
	// The payoff for a dedicated anime category (TVmaze's own genres are too
	// coarse): genre/theme/demographic/studio enriched from MyAnimeList. Rendered
	// read-only — static pills, no toggle handlers. Empty when no match was found.
	const tags = [
		...(anime.demographics ?? []),
		...(anime.genres ?? []),
		...(anime.themes ?? []),
		...(anime.studios ?? []),
	];
	const tagsField = tags.length
		? magField("Genres & details", staticChips(tags), { index: 0 })
		: null;

	// ---- fields ----
	const engagementField = magField(
		"Watch it?",
		magSelectRow(ENGAGEMENT_STATUSES, anime.engagement_status, async (value) => {
			mount(view, await updateAnime(anime.id, { engagement_status: value }), catalog);
		}, (v) => statusLabel(v, "anime")),
		{ index: 1 },
	);

	const ownershipField = magField(
		"Got it?",
		magSelectRow(OWNERSHIP_STATUSES, anime.ownership_status ?? "have_it", async (value) => {
			mount(view, await updateAnime(anime.id, { ownership_status: value }), catalog);
		}, (v) => statusLabel(v, "anime")),
		{ index: 2 },
	);

	// Entry rating — the default inherited by every season + episode unless
	// individually overridden.
	const ratingField = magField(
		"Rating (whole series)",
		magRating(anime.rating ?? null, async (value) => {
			mount(view, await setAnimeRating(anime.id, value), catalog);
		}),
		{ index: 3 },
	);

	const selectedAwards = new Set(anime.awards ?? []);
	const awardsField = magField(
		"Awards",
		magChips(AWARDS.map((a) => a.value), selectedAwards, async (next) => {
			mount(view, await setAnimeAwards(anime.id, Array.from(next)), catalog);
		}, awardLabel),
		{ index: 4 },
	);

	const noteField = magField(
		"Notes",
		magNote(anime.note ?? "", {
			maxLength: NOTE_MAX_LENGTH,
			placeholder: 'A quick note — e.g. "the OP slaps"',
			onSave: (v) => setAnimeNote(anime.id, v),
		}),
		{ index: 5 },
	);

	const seasonsField = magField("Seasons", renderSeasons(view, anime, catalog), { index: 0 });

	const groupsField = magField(
		"Groups",
		await groupsSection(anime, groups, async () =>
			mount(view, await getAnimeById(anime.id), catalog),
		),
		{ index: 1 },
	);

	const dates = magDateLine([
		["Last watched", formatDate(anime.last_engaged_date)],
		["Added", formatDate(anime.created_at)],
	]);

	const footer = magDeleteFooter("Delete from library", async () => {
		if (!confirm(`Delete "${anime.title}" from your list?`)) return;
		await deleteAnime(anime.id);
		navigate("/anime");
		offerUndoDelete("anime", anime, { reopenRoute: `/anime/${anime.id}` });
	});

	clear(view);
	view.append(
		hero,
		magSummary(anime.summary) || "",
		inTheNews(anime),
		peopleRow("anime", anime.mal_id),
		tagsField || "",
		engagementField,
		ownershipField,
		ratingField,
		awardsField,
		noteField,
		myTagsField("anime", anime, { suggestions: tagSuggestions("anime") }),
		reviewField("anime", anime),
		progressField("anime", anime),
		priceNoteField("anime", anime),
		loanField("anime", anime),
		startAgainButton("anime", anime),
		seasonsField,
		groupsField,
		dates,
		historySection(anime),
		footer,
	);

	// Restore synchronously — in the same tick as the rebuild, so the browser never
	// paints the cleared/top state (a deferred rAF restore caused a visible flash).
	window.scrollTo(0, scrollY);
}

// Read-only taxonomy pills — same chip look as magChips' inactive state, but no
// toggle/press semantics.
function staticChips(values) {
	return el(
		"div",
		{ style: { display: "flex", gap: "7px", flexWrap: "wrap" } },
		...values.map((v) =>
			el("span", {
				style: {
					fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "11px",
					letterSpacing: ".04em", textTransform: "uppercase", borderRadius: "999px",
					padding: "7px 14px", color: MAG.dim, background: "rgba(255,255,255,.07)",
					border: `1px solid ${MAG.line}`,
				},
			}, v),
		),
	);
}

function renderSeasons(anime, catalog) {
	if (!catalog) {
		return el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12px",
				color: MAG.dim, padding: "4px 0",
			},
		}, "Loading seasons…");
	}
	const seasons = (catalog.seasons ?? []).filter((s) => s.number != null);
	if (seasons.length === 0) {
		return el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "700", fontSize: "12px",
				color: MAG.dim, padding: "4px 0",
			},
		}, "No season data found.");
	}

	const list = el("div", { style: { display: "flex", flexDirection: "column", gap: "9px" } });
	for (const s of seasons) {
		list.append(seasonRow(view, anime, catalog, s));
	}
	return list;
}

// A clickable glass season row: numbered cover thumb, season title, episode +
// seen-count sub, optional rating stars, navigating to the season route. (Built
// directly rather than via magEpisodeRow, whose trailing Add button doesn't fit
// a navigate-only row.) A released, not-yet-seen season also gets a "Seen"
// button that marks the whole season watched; once seen it disappears (unsee
// from the season screen).
function seasonRow(view, anime, catalog, s) {
	const today = todayDate();
	const episodes = catalog.episodes ?? [];
	const eps = episodes.filter((e) => e.season === s.number);
	const released = seasonReleased(s, episodes, today);
	const seenCount = released
		? eps.filter((e) => {
				const epReleased = e.airdate ? e.airdate <= today : true;
				return effectiveSeen(anime, s.number, e.number, { released: epReleased });
			}).length
		: 0;
	const rating = effectiveRating(anime, s.number, null, { released });

	const thumb = magCover(anime, {
		height: "62px", width: "46px", radius: "8px",
		children: [
			el("div", {
				style: {
					position: "absolute", top: "4px", left: "4px",
					fontFamily: "'Anton', sans-serif", fontSize: "13px", color: MAG.lime,
					background: "rgba(11,11,15,.7)", borderRadius: "5px", padding: "0 5px", lineHeight: "1.3",
				},
			}, String(s.number).padStart(2, "0")),
		],
	});

	const body = el(
		"div",
		{ style: { flex: "1", minWidth: "0" } },
		el("div", {
			style: {
				fontFamily: "'Anton', sans-serif", fontSize: "18px", lineHeight: ".95",
				textTransform: "uppercase", color: "#fff",
				overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
			},
		}, s.name || `Season ${s.number}`),
		el("div", {
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "800", fontSize: "9px",
				letterSpacing: ".06em", textTransform: "uppercase", color: MAG.dim, marginTop: "3px",
			},
		}, released
			? `${eps.length} episode${eps.length === 1 ? "" : "s"}${eps.length ? ` · ${seenCount}/${eps.length} seen` : ""}`
			: (seasonPremiereDate(s, episodes) ? `Airs ${formatReleaseDate(seasonPremiereDate(s, episodes))}` : "Not yet aired")),
	);

	const seasonSeen = effectiveSeen(anime, s.number, null, { released });

	const right = el("div", { style: { display: "flex", alignItems: "center", gap: "8px", flex: "none" } });
	if (rating != null) {
		right.append(el("span", {
			style: { fontFamily: "'Anton', sans-serif", fontSize: "15px", color: MAG.yellow, lineHeight: "1" },
		}, `★${rating}`));
	}
	// A released, not-yet-seen season gets a "Seen" button (marks the whole season
	// watched). stopPropagation so tapping it doesn't also navigate into the row.
	if (released && !seasonSeen) {
		right.append(magButton("Seen", {
			variant: "primary",
			onClick: async (e) => {
				e?.stopPropagation?.();
				mount(view, await setSeasonSeen(anime.id, s.number, true), catalog);
			},
		}));
	}
	right.append(el("span", { style: { color: MAG.dim, fontSize: "20px", lineHeight: "1" } }, "›"));

	return magGlass(
		[thumb, body, right],
		{
			role: "button",
			onClick: () => navigate(`/anime/${anime.id}/season/${s.number}`),
			style: { display: "flex", alignItems: "center", gap: "12px", padding: "10px 12px" },
		},
	);
}
